import BaseService from '../../../base/base_service';
import {TimeSpan} from "@sparkware/app-utils";
import {SYSTEM_EVENTS} from '../../../events/events';
import Event from '../../../utils/event';
import _ from '../../../utils/underscore';
import JackpotFeedsServiceDependenciesFactory from './jackpot_feeds_service_dependencies_factory';
import IJackpotFeedsService, {
    IJackpotData,
    IGamesJackpotsData,
    IJackpotFeedsServiceParams,
    IJackpotDataById
} from "./jackpot_feeds_service_interface";
import JackpotFeedsReader from './jackpot_feeds_reader';
import GamesJackpotsPublisher from './games_jackpots_publisher';
import  {IObservableSubscription} from "../../../utils/observable";
import {NullableNumber} from "../../../../../common/types/nullable-types";
import {SetTimeoutType} from "../../../../../common/types/set-timeout-type";

const throttleTimeout = 300;

export default class JackpotFeedsService extends BaseService implements IJackpotFeedsService {
    static CLASS = 'JackpotFeedsService';

    private _getGameJackpotUrl: string;
    private _params: IJackpotFeedsServiceParams;
    private _getJackpotsUrl: string;
    private _refreshInterval: TimeSpan;

    private _jackpotData: IGamesJackpotsData | null = null;

    private _dependenciesFactory: JackpotFeedsServiceDependenciesFactory;
    private _gamesJackpotsPublisher: GamesJackpotsPublisher;
    private _jackpotFeedsReader: JackpotFeedsReader;

    private intervalReference: IObservableSubscription | undefined = undefined;
    private _isRequestPaused: boolean = false;
    private _timeoutGuard: SetTimeoutType;



    init(params: IJackpotFeedsServiceParams) {
        super.init(params);
        this._params = params;
        this._getJackpotsUrl = params.getJackpotsUrl;
        this._getGameJackpotUrl = params.getGameJackpotUrl;
        this._refreshInterval = TimeSpan.parse(params.refreshInterval);
        this._dependenciesFactory = params.dependenciesFactory;
        if(_.isNullOrUndefined(params.disableJackpotFeeds) || !params.disableJackpotFeeds) {
          this._subscribe();
        }
    }

    _subscribe() {
        this.onSystemEvent(SYSTEM_EVENTS.ON_INIT_COMPLETE, this._onInitComplete);
        this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._onOAuthStateChanged);
        this.onSystemEvent(SYSTEM_EVENTS.CLOSE_GAME, this._onCloseGame);
    }

    get dependenciesFactory() {
        if(!this._dependenciesFactory) {
            this._dependenciesFactory = JackpotFeedsServiceDependenciesFactory.create({
                serviceFactory: this.serviceFactory,
                getJackpotsUrl: this._getJackpotsUrl,
                getGameJackpotUrl: this._getGameJackpotUrl,
                jackpotsRefreshInterval: this._refreshInterval
            });
        }

        return this._dependenciesFactory;
    }

    private _createIntervalReference() : IObservableSubscription {
        return this.dependenciesFactory
            .createFeedsObservable(this._refreshInterval.totalMilliseconds)
            .forEach(() => this._readAndPublish());
    }

    private _onInitComplete() {
        this._gamesJackpotsPublisher = this.dependenciesFactory.createGamesJackpotsPublisher();
        this._jackpotFeedsReader = this.dependenciesFactory.createJackpotFeedsReader();
        this._readAndPublish(true);
        this._startOrStopJackpotRequest();

        Event.on(SYSTEM_EVENTS.APP_PAUSED, () => {
            this._isRequestPaused = true;
            this._applyGuardedRequestDecision();
        });

        Event.on(SYSTEM_EVENTS.APP_RESUMED, () => {
            this._isRequestPaused = false;
            this._applyGuardedRequestDecision();
        });
    }

    private _onOAuthStateChanged() {
        this._readAndPublish(true);
    }

    private _applyGuardedRequestDecision() {
        if (this._timeoutGuard) {
            this._timeoutGuard = undefined;
        }

        this._timeoutGuard = setTimeout(() => {
            this._timeoutGuard = undefined;
            this._startOrStopJackpotRequest();
        }, throttleTimeout);
    }

    private _startOrStopJackpotRequest() {
        if (this._isRequestPaused) {
            if (this.intervalReference) {
                this.intervalReference.dispose();
                this.intervalReference = undefined;
            }
        } else {
            if (!this.intervalReference) {
                this.intervalReference = this._createIntervalReference();
            }
        }
    }


    private _onCloseGame(closedGame: any) {
        let gameType: number = closedGame.gameType;
        this.serviceFactory.gamesInformationService()
            .getGamesInfo([gameType])
            .then(gamesInfo => gamesInfo[gameType])
            .then(gameInfo => {
                if(gameInfo /*&& gameInfo.hasJackpot*/) {
                    return this._jackpotFeedsReader.readGameJackpot(gameType)
                        .then(gameJackpot => {
                            this._gamesJackpotsPublisher.forcePublish(gameJackpot);
                        }).catch((err)=> {
                            this.logger.error('Fail readGameJackpot jackpots', err);
                        });
                }
                return Promise.resolve();
            });
    }

    private _readAndPublish(force = false) {
        /**
         * TODO: Make jackpotFeedsReader.readAll typesafe so data will be type inferred and not declared
         * {
         *    GamesJackpots: {[gameType: string]: IGameJackpotData};
         *    TotalJackpotsInCents: number;
         * }
         */
        this._jackpotFeedsReader.readAll().then((data) => {
            let jackpotData: IGamesJackpotsData = {
                ByGameType: data["GamesJackpots"],
                ById: this._buildJackpotDataById(data["GamesJackpots"]),
                TotalJackpotsInCents: data["TotalJackpotsInCents"]
            };

            this._publishJackpotsById(jackpotData.ById);

            Event.fire(SYSTEM_EVENTS.LIVE_FEEDS_TOTAL_JACKPOTS_UPDATED, jackpotData.TotalJackpotsInCents);
            //this.serviceFactory.clockService().setServerTime(data.ServerTime);
            //this.serviceFactory.clockService().setRegulationOffset(data.RegulationOffset);
            //Event.fire(SYSTEM_EVENTS.CLOCK_UPDATED, {ServerTime:data.ServerTime,RegulationOffset:data.RegulationOffset});

            if(force) {
                this._gamesJackpotsPublisher.forcePublish(jackpotData.ByGameType);
            } else {
                this._gamesJackpotsPublisher.delayPublish(jackpotData.ByGameType);
            }

            this._jackpotData = jackpotData;

        }).catch((err)=> {
            this.logger.warn('Fail _readAndPublish jackpots', err);
        });
    }

    /**
     * if any of the jackpots provided in param it's different from the existing one FIRE EVENT
     *
     * Caveat: This approach has a problem. It doesn't treat if a jackpot should be removed from the Map
     * TODO: Treat the case when a jackpot should be removed.
     */
    private _publishJackpotsById(jackpotData: IGamesJackpotsData["ById"]) {
        let jackpotUpdates: IGamesJackpotsData["ById"] = {};
        Object.keys(jackpotData).forEach(jpIdString => {
            // Although we iterate through a Record<number, IJackpotDataById> the value in this forEach is string
            // The reason is here: https://github.com/microsoft/TypeScript/issues/32811
            // So we make a parsing to number even if not necessary
            let jpId = parseInt(jpIdString);
            let jackpot = jackpotData[jpId];

            let currentJackpot = this._jackpotData && this._jackpotData.ById[jpId];
            if (!currentJackpot || !this.jackpotIsEqual(currentJackpot.Jackpot, jackpot.Jackpot)) {
                jackpotUpdates[jpId] = jackpot;
            }
        });
        if (Object.keys(jackpotUpdates).length > 0) {
            Event.fire(SYSTEM_EVENTS.LIVE_FEEDS_JACKPOTS_BY_ID_UPDATED, jackpotData);
        }
    }

    /**
     * Iterate through all games jackpot (an object dictionary with gameType as key and IJackpotDataByGame as value)
     * Then, iterate through all game's jackpots.
     *
     * Take each and add it to the ById map, putting the gameType into the gameTypes array
     */
    private _buildJackpotDataById(gamesJackpots: IGamesJackpotsData["ByGameType"]) {
        let gameTypes = Object.keys(gamesJackpots);
        let jackpotDataById: {[jackpotId: number]: IJackpotDataById} = {};
        gameTypes.forEach(gameType => {
            let gameJackpotData = gamesJackpots[gameType];
            if(!gameJackpotData || !gameJackpotData.Jackpots) {
                return;
            }

            gameJackpotData.Jackpots.forEach(jackpot => {
                let currentJackpot = jackpotDataById[jackpot.JackpotId];
                if (currentJackpot) {
                    currentJackpot.GameTypes.push(gameType);
                } else {
                    currentJackpot = {
                        Jackpot: jackpot,
                        GameTypes: [gameType]
                    };
                }
                jackpotDataById[jackpot.JackpotId] = currentJackpot;
            });
        });
        return jackpotDataById;
    }

    getJackpotsByGameType() {
        if(!this._jackpotData) {
            return null;
        }
        return this._jackpotData.ByGameType;
    }

    getJackpotAmountInCentsByGame(gameType: number): NullableNumber {
        if(!this._jackpotData) {
            return null;
        }
        let gameJackpot = this._jackpotData.ByGameType[gameType];
        if(!gameJackpot) {
            return null;
        }
        return gameJackpot.AmountInCents;
    }

    jackpotFeedsDisabled() : boolean{
        return this._params && this._params.disableJackpotFeeds;
    }

    getTotalJackpotsInCents(): number | null {
        if(this._jackpotData) {
            return this._jackpotData.TotalJackpotsInCents;
        } else {
            return null;
        }

    }

    getJackpotsById() {
        if(!this._jackpotData) {
            return null;
        }
        return this._jackpotData.ById;
    }

    jackpotIsEqual(firstJP: IJackpotData, secondJP: IJackpotData): boolean {
        return (
            firstJP && secondJP &&
            (firstJP.AmountInCents === secondJP.AmountInCents) &&
            (firstJP.JackpotId === secondJP.JackpotId) &&
            (firstJP.JackpotState === secondJP.JackpotState) &&
            (firstJP.LastWinAmount === secondJP.LastWinAmount) &&
            (firstJP.MustHitTime === secondJP.MustHitTime)
        );
    }
}
